/**
 * The sentence an employee reads about their uniform size.
 *
 * The size shown is the one the employee ASKED FOR — their preference, theirs
 * to maintain — never the one they were handed. The two differ for two
 * reasons this screen cannot tell apart (the warehouse substituted, or they
 * changed their size since), so showing both side by side reads as a
 * discrepancy when half the time it is the system working.
 *
 * Every field is treated as optional. These keys arrive from an endpoint that
 * may not be deployed in front of this build yet, and a page that throws
 * because `can_change` is missing is worse than one that says nothing about
 * the window. Same defensive contract as `describeReplacement` in the bulk
 * issuance module.
 */
import { formatDateHandler } from 'utils/common';

/**
 * A date in the page's own format, or null if it cannot be read.
 *
 * The API sends MM/DD/YYYY and every other date on this screen renders as
 * "Issued Sep 15, 2026"; two formats on one card read as two different kinds
 * of date. date-fns raises on an unparseable value, so a malformed field
 * would otherwise take the page down rather than losing one line of detail.
 */
const on = (value) => {
  if (!value) return null;
  try {
    const text = formatDateHandler(value);
    return text || null;
  } catch (e) {
    return null;
  }
};

const LOCK_TEXT = {
  window_closed: 'Confirmation closed',
  po_lead_time: 'Locked for ordering',
  single_size: 'Only one size',
};

/**
 * @returns {{tone, text, detail}|null} null when there is nothing to say —
 * no replacement due, or the backend did not send these keys.
 */
export const describeWindow = (row) => {
  if (!row || !row.next_due_date) return null;

  const deadline = on(row.change_deadline);
  const due = on(row.next_due_date);
  const opens = on(row.window_opens_on);

  if (row.needs_confirmation) {
    const days = row.days_left_to_change;
    let text = 'Confirm your size';
    if (days === 0) text = 'Last day to confirm';
    else if (typeof days === 'number') {
      text = `${days} ${days === 1 ? 'day' : 'days'} left to confirm`;
    }
    return {
      tone: 'open',
      text,
      detail: deadline ? `Closes ${deadline}` : null,
    };
  }

  if (row.confirmed_for_this_cycle) {
    return {
      tone: 'answered',
      text: 'Confirmed',
      detail: due ? `Next uniform due ${due}` : null,
    };
  }

  if (row.can_change === false) {
    // A uniform that comes in one size was never open, so it is not
    // "closed": there is no deadline to report and nothing the employee
    // missed. Its own tone keeps it out of the amber-to-grey progression
    // the real window states move through.
    if (row.lock_reason === 'single_size') {
      return { tone: 'fixed', text: LOCK_TEXT.single_size, detail: null };
    }
    return {
      // Naming which limit closed it matters: "you missed the window" and
      // "your uniform is already on order" call for different next steps,
      // and both beat a bare "locked".
      tone: 'closed',
      text: LOCK_TEXT[row.lock_reason] || 'Size locked',
      detail: deadline ? `Closed ${deadline}` : null,
    };
  }

  return {
    tone: 'upcoming',
    text: 'Size change open',
    detail: opens ? `We'll ask you on ${opens}` : null,
  };
};